import React, { useState, useRef } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { Modal, Button, Input, Checkbox, Icon } from '@arnsbogroup/react-voicearchive'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useDropzone } from 'react-dropzone'
import classNames from 'classnames'
import { aiFileMessages } from 'components/AiFileChip'
import { MAX_UPLOAD_BYTES, formatBytes } from 'utils'
import messages from './messages'

/**
 * POSTs one file to uploadspeak with progress (fetch has no upload progress).
 * Resolves with the error messages, empty on success. Never rejects.
 */
const uploadFile = (body, onProgress, fallbackError) => new Promise((resolve) => {
  const xhr = new XMLHttpRequest()

  xhr.upload.onprogress = (event) => {
    if (event.lengthComputable) {
      onProgress(Math.round((event.loaded / event.total) * 100))
    }
  }
  xhr.responseType = 'json'
  xhr.onload = () => {
    const { success, errors } = xhr.response || {}
    const messages = Object.values(errors || {})

    resolve(success ? [] : (messages.length ? messages : [fallbackError]))
  }
  xhr.onerror = () => resolve([fallbackError])

  xhr.open('POST', '/ordertask/uploadspeak.json', true)
  xhr.send(body)
})

const UploadRow = ({ entry, onRemove }) => {
  const { file, status, progress, errors } = entry
  // The bytes are sent; the server still stores (or extracts) the file.
  const processing = status === 'uploading' && progress === 100

  return (
    <div className="border border-gray-300 rounded p-2 space-y-1">
      <div className="flex justify-between items-center space-x-2 text-sm">
        <div className="truncate font-mono font-medium text-primary" title={file.name}>{file.name}</div>
        <div className="flex items-center space-x-2 shrink-0 text-xs text-gray-500">
          <span>{formatBytes(file.size)}</span>
          { status === 'done' && <Icon className="text-positive" icon={['fas', 'check']} /> }
          { status === 'error' && <Icon className="text-negative" icon={['fas', 'exclamation-triangle']} /> }
          { onRemove &&
            <Icon
              className="cursor-pointer hover:text-gray-700"
              icon={['fal', 'trash']}
              onClick={onRemove}
            />
          }
        </div>
      </div>

      { status !== 'queued' && status !== 'error' &&
        <div className="flex items-center space-x-2">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={classNames('h-2 rounded-full transition-all', {
                'bg-primary': status === 'uploading',
                'bg-positive': status === 'done',
                'animate-pulse': processing,
              })}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="w-10 text-right text-xs text-gray-500">{progress}%</span>
        </div>
      }

      { status === 'error' &&
        <div className="text-xs text-negative">{errors.join(', ')}</div>
      }
    </div>
  )
}

/**
 * Upload files to a task with a progress bar per file, optionally marked as AI-generated.
 *
 * - isSource: upload as source files (the task's file list) instead of deliverables
 *   uploaded on behalf of the voice talent (the task status action).
 * - initialFiles: files already picked, e.g. dropped on the task's file list.
 *
 * Resolves with { refetchFiles } once something was uploaded and the modal closes.
 */
const UploadTaskFilesModal = ({ task, isSource = false, initialFiles = [] }) => {
  const { formatMessage } = useIntl()
  const modal = useModal()
  const nextId = useRef(0)

  const toEntries = (files) => files.map((file) => {
    const tooLarge = file.size > MAX_UPLOAD_BYTES

    return {
      id: nextId.current++,
      file,
      tooLarge,
      status: tooLarge ? 'error' : 'queued',
      progress: 0,
      errors: tooLarge
        ? [formatMessage(messages.fileTooLarge, { maxSize: formatBytes(MAX_UPLOAD_BYTES, 0) })]
        : [],
    }
  })

  const [entries, setEntries] = useState(() => toEntries(initialFiles))
  const [wordCount, setWordCount] = useState(0)
  const [markAsAi, setMarkAsAi] = useState(false)

  const uploading = entries.some(({ status }) => status === 'uploading')
  const uploadable = entries.filter(({ status, tooLarge }) => !tooLarge && ['queued', 'error'].includes(status))
  const uploadedCount = entries.filter(({ status }) => status === 'done').length

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => setEntries((prev) => [...prev, ...toEntries(files)]),
    disabled: uploading,
  })

  const update = (id, changes) => setEntries((prev) => prev.map((entry) => entry.id === id ? { ...entry, ...changes } : entry))

  const close = () => {
    if (uploading) {
      return
    }
    if (uploadedCount > 0) {
      modal.resolve({ refetchFiles: true })
    }
    modal.remove()
  }

  const onSubmit = (event) => {
    event.preventDefault()
    if (uploading || uploadable.length === 0) {
      return
    }

    const uploads = uploadable.map(({ id, file }) => {
      update(id, { status: 'uploading', progress: 0, errors: [] })

      const body = new FormData()
      body.append('taskid', task.uid)
      body.append('addFileIsSource', isSource ? 1 : 0)
      body.append('addFileWordcount', isSource ? 0 : wordCount)
      body.append('addFileFile', file)
      body.append('addFileIsAi', markAsAi ? 1 : 0)

      return uploadFile(body, (progress) => update(id, { progress }), formatMessage(messages.uploadFailed))
        .then((errors) => {
          update(id, errors.length ? { status: 'error', errors } : { status: 'done', progress: 100 })
          return errors.length === 0
        })
    })

    Promise.all(uploads).then((results) => {
      const succeeded = results.filter(Boolean).length
      if (succeeded > 0) {
        window?.toastr.success(formatMessage(messages.uploaded, { count: succeeded }))
      }
      // Failed files stay listed with their error so they can be retried or removed.
      if (succeeded === results.length) {
        modal.resolve({ refetchFiles: true })
        modal.remove()
      }
    })
  }

  return (
    <Modal
      show={modal.visible}
      onClose={close}
      onOutsideClick={close}
      title={formatMessage(isSource ? messages.sourceTitle : messages.deliverableTitle)}
      dialogPanelClasses="max-w-lg"
    >
      <form className="space-y-4 mt-4" onSubmit={onSubmit}>
        { !isSource &&
          <div className="flex justify-between items-center">
            <label className="font-medium text-sm text-gray-600">
              <FormattedMessage {...messages.wordCount} />
            </label>

            <div className="w-1/4">
              <Input
                size="sm"
                type="text"
                value={wordCount}
                disabled={uploading}
                onChange={(event) => setWordCount(event.target.value)}
              />
            </div>
          </div>
        }

        { entries.length > 0 &&
          <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto">
            { entries.map((entry) => (
              <UploadRow
                key={entry.id}
                entry={entry}
                onRemove={!uploading && entry.status !== 'done'
                  ? () => setEntries((prev) => prev.filter(({ id }) => id !== entry.id))
                  : null}
              />
            )) }
          </div>
        }

        <div
          className={classNames('w-full h-20 flex items-center justify-center border border-gray-300 border-dashed shadow-inner', {
            'cursor-pointer': !uploading,
            'opacity-50': uploading,
          })}
          {...getRootProps()}
        >
          <input {...getInputProps()} />

          <div className="w-full text-sm text-center px-2 py-4 text-gray-500">
            { isDragActive
              ? <FormattedMessage {...messages.dropzoneActive} />
              : <FormattedMessage
                  {...messages.dropzone}
                  values={{ link: (chunks) => <span className="text-primary font-medium">{chunks}</span> }}
                />
            }
          </div>
        </div>

        {/* Off by default, and per modal: the next upload is never flagged by accident. */}
        <Checkbox
          label={formatMessage(aiFileMessages.markUploadsAsAi)}
          checked={markAsAi}
          disabled={uploading}
          onChange={() => setMarkAsAi((value) => !value)}
          labelClassNames="text-sm text-gray-600"
        />

        <ol className="text-sm text-gray-600">
          <FormattedMessage
            {...messages.sideEffects}
            values={{ li: (chunks) => <li>{chunks}</li> }}
          />
        </ol>

        <div className="flex justify-end items-center space-x-2">
          <Button type="submit" size="sm" disabled={uploading || uploadable.length === 0}>
            <FormattedMessage {...(uploading ? messages.uploading : messages.submit)} />
          </Button>

          <Button type="button" size="sm" onClick={close} variant="outline" disabled={uploading}>
            <FormattedMessage {...(uploadedCount > 0 ? messages.close : messages.cancel)} />
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default NiceModal.create(UploadTaskFilesModal)
